import { useEffect, useRef, useState } from "react";

import Hls from "hls.js";

import { VideoRecording } from "@app/common/types";

const DEFAULT_HLS_CONFIG = {
  autoStartLoad: true,
  capLevelToPlayerSize: true,
};

const getXhrSetup = (recordingToken: string) => (xhr: XMLHttpRequest) => {
  xhr.setRequestHeader("Authorization", `Bearer ${recordingToken}`);
};

interface UseHlsVideoOpts {
  startTimestamp?: number;
  loadVideo?: boolean;
}

const useHlsVideo = (recordingInfo: VideoRecording, opts?: UseHlsVideoOpts) => {
  const [isLoading, setIsLoading] = useState(false);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [hlsDuration, setHlsDuration] = useState<number>(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const hls = useRef<Hls | null>(null);

  const { playlistUrl, recordingAccessToken } = recordingInfo;
  const shouldLoadVideo = opts?.loadVideo ?? true;

  const startPosition = opts?.startTimestamp ?? 0;

  const videoUniqueKey = `$${playlistUrl}-${recordingAccessToken}-${startPosition}`;

  useEffect(() => {
    setVideo(videoRef.current);
  }, [videoRef.current]);

  useEffect(() => {
    if (!video) return;

    if (hls.current) {
      setIsLoading(true);
      hls.current.destroy();
    }

    if (!shouldLoadVideo) return;

    hls.current = new Hls({
      ...DEFAULT_HLS_CONFIG,
      startPosition,
      xhrSetup: getXhrSetup(recordingAccessToken),
    });

    // load stream
    hls.current.loadSource(playlistUrl);

    // bind instances together
    hls.current.attachMedia(video);

    hls.current?.on(Hls.Events.MEDIA_ATTACHED, () => {
      setIsLoading(false);
    });

    // get duration from loaded HLS source for coaching videos
    hls.current?.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
      setHlsDuration(data.details.totalduration);
    });
  }, [video, shouldLoadVideo, videoUniqueKey]);

  return {
    video,
    videoRef,
    isLoading,
    hlsDuration,
  };
};

export default useHlsVideo;
